import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { loadGoogleAnalytics, trackPageview } from "@/lib/analytics";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import StudentDashboard from "@/pages/StudentDashboard";
import Assessment from "@/pages/Assessment";
import Report from "@/pages/Report";
import SampleReportPublic from "@/pages/SampleReportPublic";
import ScienceDeepDive from "@/pages/ScienceDeepDive";
import AptitudeIntro from "@/pages/AptitudeIntro";
import AptitudeTest from "@/pages/AptitudeTest";
import AptitudeReport from "@/pages/AptitudeReport";

import MarketingDashboard from "@/pages/marketing/MarketingDashboard";
import MarketingStudents from "@/pages/marketing/MarketingStudents";
import MarketingStudentReport from "@/pages/marketing/MarketingStudentReport";
import MarketingLink from "@/pages/marketing/MarketingLink";
import MarketingInbox from "@/pages/marketing/MarketingInbox";
import MarketingSampleReport from "@/pages/marketing/MarketingSampleReport";
import MarketingAptitudeReport from "@/pages/marketing/MarketingAptitudeReport";
import DemoMode from "@/pages/marketing/DemoMode";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminExecutives from "@/pages/admin/AdminExecutives";
import AdminStudents from "@/pages/admin/AdminStudents";
import AdminStudentReport from "@/pages/admin/AdminStudentReport";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminInbox from "@/pages/admin/AdminInbox";
import AdminSampleReport from "@/pages/admin/AdminSampleReport";
import AdminHowItWorks from "@/pages/admin/AdminHowItWorks";
import AdminAptitudeReport from "@/pages/admin/AdminAptitudeReport";
import AdminQuestionBank from "@/pages/admin/AdminQuestionBank";
import Contact from "@/pages/Contact";

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    loadGoogleAnalytics();
  }, []);

  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/start" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/sample-report" element={<SampleReportPublic />} />
      <Route path="/science" element={<ScienceDeepDive />} />

      <Route path="/onboarding" element={<ProtectedRoute roles={["STUDENT"]}><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute roles={["STUDENT"]}><StudentDashboard /></ProtectedRoute>} />
      <Route path="/assessment" element={<ProtectedRoute roles={["STUDENT"]}><Assessment /></ProtectedRoute>} />
      <Route path="/report" element={<ProtectedRoute roles={["STUDENT"]}><Report /></ProtectedRoute>} />
      <Route path="/aptitude" element={<ProtectedRoute roles={["STUDENT"]}><AptitudeIntro /></ProtectedRoute>} />
      <Route path="/aptitude/test" element={<ProtectedRoute roles={["STUDENT"]}><AptitudeTest /></ProtectedRoute>} />
      <Route path="/aptitude/report" element={<ProtectedRoute roles={["STUDENT"]}><AptitudeReport /></ProtectedRoute>} />

      <Route path="/me" element={<ProtectedRoute roles={["MARKETING_EXECUTIVE"]}><MarketingDashboard /></ProtectedRoute>} />
      <Route path="/me/students" element={<ProtectedRoute roles={["MARKETING_EXECUTIVE"]}><MarketingStudents /></ProtectedRoute>} />
      <Route path="/me/students/:studentId/report" element={<ProtectedRoute roles={["MARKETING_EXECUTIVE"]}><MarketingStudentReport /></ProtectedRoute>} />
      <Route path="/me/link" element={<ProtectedRoute roles={["MARKETING_EXECUTIVE"]}><MarketingLink /></ProtectedRoute>} />
      <Route path="/me/inbox" element={<ProtectedRoute roles={["MARKETING_EXECUTIVE"]}><MarketingInbox /></ProtectedRoute>} />
      <Route path="/me/students/:studentId/aptitude-report" element={<ProtectedRoute roles={["MARKETING_EXECUTIVE"]}><MarketingAptitudeReport /></ProtectedRoute>} />
      <Route path="/me/sample-report" element={<ProtectedRoute roles={["MARKETING_EXECUTIVE", "SUPER_ADMIN"]}><MarketingSampleReport /></ProtectedRoute>} />
      <Route path="/me/demo" element={<ProtectedRoute roles={["MARKETING_EXECUTIVE", "SUPER_ADMIN"]}><DemoMode /></ProtectedRoute>} />

      <Route path="/admin" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/executives" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminExecutives /></ProtectedRoute>} />
      <Route path="/admin/students" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminStudents /></ProtectedRoute>} />
      <Route path="/admin/students/:studentId/report" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminStudentReport /></ProtectedRoute>} />
      <Route path="/admin/inbox" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminInbox /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminAudit /></ProtectedRoute>} />
      <Route path="/admin/sample-report" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminSampleReport /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/how-it-works" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminHowItWorks /></ProtectedRoute>} />
      <Route path="/admin/questions" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminQuestionBank /></ProtectedRoute>} />
      <Route path="/admin/students/:studentId/aptitude-report" element={<ProtectedRoute roles={["SUPER_ADMIN"]}><AdminAptitudeReport /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
